import { RendererFactory2, Renderer2 } from '@angular/core';
import { take } from 'rxjs/operators';
import { CaasContentService } from '../services/caas-content.service';

// Extend the global Window interface to include dataLayer.
declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

export function loadScriptsFactory(
  caasContentService: CaasContentService,
  rendererFactory: RendererFactory2
): () => Promise<void> {
  return () =>
    new Promise<void>((resolve) => {
      // Guard against SSR.
      if (typeof window === 'undefined') {
        resolve();
        return;
      }

      // Ensure the dataLayer is initialized as recommended by GTM docs.
      if (!window.dataLayer) {
        window.dataLayer = [];
      }

      caasContentService
        .getHeader()
        .pipe(take(1))
        .subscribe({
          next: (scriptContent: string | string[] | null) => {
            const renderer: Renderer2 = rendererFactory.createRenderer(
              null,
              null
            );
            const loadPromises: Promise<void>[] = [];

            if (scriptContent) {
              if (Array.isArray(scriptContent)) {
                scriptContent.forEach((script) => {
                  loadPromises.push(...insertScript(script, renderer));
                });
              } else {
                loadPromises.push(...insertScript(scriptContent, renderer));
              }
            }

            // Wait for any external scripts (with src) to load or error before resolving.
            Promise.all(loadPromises).then(() => {
              // console.log('dataLayer:', window.dataLayer);
              resolve();
            });
          },
          error: (err: unknown) => {
            console.error('Error loading scripts from backend:', err);
            resolve();
          },
        });
    });
}

function insertScript(
  scriptContent: string,
  renderer: Renderer2
): Promise<void>[] {
  const loadPromises: Promise<void>[] = [];

  if (scriptContent.includes('<script')) {
    // Use DOMParser to convert the HTML snippet into a document.
    const parser = new DOMParser();
    const doc = parser.parseFromString(scriptContent, 'text/html');

    // Process <noscript> elements: insert them at the beginning of the <body>.
    const noscriptElements = doc.getElementsByTagName('noscript');
    for (let i = 0; i < noscriptElements.length; i++) {
      const noscriptTag = noscriptElements[i];
      const newNoscript = renderer.createElement('noscript');
      newNoscript.innerHTML = noscriptTag.innerHTML;
      renderer.insertBefore(
        document.body,
        newNoscript,
        document.body.firstChild
      );
    }

    // Process <script> elements: recreate them and append to the <head>.
    const scriptElements = doc.getElementsByTagName('script');
    for (let i = 0; i < scriptElements.length; i++) {
      const scriptTag = scriptElements[i];
      const newScript = renderer.createElement('script') as HTMLScriptElement;
      newScript.type = 'text/javascript';

      // Copy all attributes from the parsed script.
      for (let j = 0; j < scriptTag.attributes.length; j++) {
        const attr = scriptTag.attributes[j];
        newScript.setAttribute(attr.name, attr.value);
      }
      // Copy inline script content.
      newScript.text = scriptTag.textContent || '';

      // Append the new script to <head>.
      renderer.appendChild(document.head, newScript);

      // If this is an external script, attach load and error handlers.
      if (newScript.src) {
        newScript.async = true;
        const promise = new Promise<void>((resolve) => {
          newScript.addEventListener('load', () => resolve());
          newScript.addEventListener('error', () => {
            // Remove the script element on error to prevent execution of an error page.
            if (newScript.parentNode) {
              newScript.parentNode.removeChild(newScript);
            }
            resolve();
          });
        });
        loadPromises.push(promise);
      }
    }
  } else {
    // Otherwise, treat the string as either a URL or inline JavaScript.
    const scriptEl: HTMLScriptElement = renderer.createElement('script');
    scriptEl.type = 'text/javascript';
    if (scriptContent.startsWith('http')) {
      // External script URL.
      scriptEl.src = scriptContent;
      scriptEl.async = true;
      const promise = new Promise<void>((resolve) => {
        scriptEl.addEventListener('load', () => resolve());
        scriptEl.addEventListener('error', () => {
          if (scriptEl.parentNode) {
            scriptEl.parentNode.removeChild(scriptEl);
          }
          resolve();
        });
      });
      loadPromises.push(promise);
    } else {
      // Inline JavaScript: remove any stray <script> tags.
      const inlineContent = scriptContent
        .replace(/<\/?script[^>]*>/gi, '')
        .trim();
      scriptEl.text = inlineContent;
    }
    renderer.appendChild(document.head, scriptEl);
  }

  return loadPromises;
}

export {};
